import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle, XCircle, Clock, Server, Database, Mail, Shield } from 'lucide-react';
import healthService from '../../services/healthService';
import LoadingSpinner from '../ui/LoadingSpinner';
import serviceConfig from '../../config/serviceConfig';

const HealthDashboard = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchHealthData = async () => {
    try {
      setLoading(true);
      const data = await healthService.getSystemHealth();
      setHealthData(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError('Failed to fetch system health data');
      console.error('Health check error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthData();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealthData, 30000);

    return () => clearInterval(interval);
  }, []);

  // Get service icon based on service name
  const getServiceIcon = (serviceName) => {
    const iconMap = {
      'auth-service': Shield,
      'admin-service': Server,
      'student-service': Server,
      'core-service': Server,
      'api-gateway': Server,
      'mongodb': Database,
      'redis': Database,
      'mailgun': Mail
    };
    return iconMap[serviceName] || Server;
  };

  // Get status icon and styling
  const getStatusDisplay = (status) => {
    switch (status?.toUpperCase()) {
      case 'UP':
        return {
          icon: CheckCircle,
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          borderColor: 'border-green-200',
          iconColor: 'text-green-600'
        };
      case 'DOWN':
        return {
          icon: XCircle,
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          borderColor: 'border-red-200',
          iconColor: 'text-red-600'
        };
      case 'DISABLED':
        return {
          icon: Clock,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          borderColor: 'border-gray-200',
          iconColor: 'text-gray-600'
        };
      default:
        return {
          icon: AlertTriangle,
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          borderColor: 'border-yellow-200',
          iconColor: 'text-yellow-600'
        };
    }
  };

  const renderServiceStatus = (serviceName, serviceData) => {
    const status = serviceData?.status || 'UNKNOWN';
    const displayName = healthService.formatServiceName(serviceName);
    const ServiceIcon = getServiceIcon(serviceName);
    const statusDisplay = getStatusDisplay(status);
    const StatusIcon = statusDisplay.icon;

    return (
      <div key={serviceName} className="bg-white border border-slate-200 p-4 shadow-sm azure-hover transition-all duration-200" style={{ borderRadius: '0' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 border border-blue-200" style={{ borderRadius: '0' }}>
              <ServiceIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h4 className="text-sm font-medium azure-text">{displayName}</h4>
              <p className="text-xs text-blue-700">{serviceName}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <StatusIcon className={`h-4 w-4 ${statusDisplay.iconColor}`} />
            <span className={`inline-flex items-center px-2 py-1 text-xs font-medium ${statusDisplay.bgColor} ${statusDisplay.textColor} border ${statusDisplay.borderColor}`} style={{ borderRadius: '0' }}>
              {status}
            </span>
          </div>
        </div>

        {serviceData?.error && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200" style={{ borderRadius: '0' }}>
            <p className="text-xs text-red-700">
              <span className="font-medium">Error:</span> {serviceData.error}
            </p>
          </div>
        )}

        {serviceData?.details && serviceData.details.status && (
          <div className="mt-2 p-2 bg-blue-50 border border-blue-200" style={{ borderRadius: '0' }}>
            <p className="text-xs text-blue-700">
              <span className="font-medium">Details:</span> {serviceData.details.status}
            </p>
          </div>
        )}

        {serviceData?.database && (
          <div className="mt-2 p-2 bg-gray-50 border border-gray-200" style={{ borderRadius: '0' }}>
            <p className="text-xs text-gray-700">
              <span className="font-medium">Database:</span> {serviceData.database}
            </p>
          </div>
        )}

        {serviceData?.provider && (
          <div className="mt-2 p-2 bg-gray-50 border border-gray-200" style={{ borderRadius: '0' }}>
            <p className="text-xs text-gray-700">
              <span className="font-medium">Provider:</span> {serviceData.provider}
            </p>
          </div>
        )}
      </div>
    );
  };

  const getOverallStatus = () => {
    if (!healthData?.overall) return 'UNKNOWN';
    return healthData.overall.status;
  };

  if (loading && !healthData) {
    return (
      <div className="p-6">
        <div className="page-header-compact">
          <h1>System Health Dashboard</h1>
          <p>Monitor the status of all system components and services</p>
        </div>
        <LoadingSpinner
          size="md"
          message="Loading system health data..."
          className="py-12"
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="page-header-compact">
        <div className="flex items-center justify-between">
          <div>
            <h1>System Health Dashboard</h1>
            <p>Monitor the status of all system components and services</p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={fetchHealthData}
              className="btn-primary flex items-center space-x-2"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            {lastUpdated && (
              <span className="text-xs text-blue-700 font-medium">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 flex items-center justify-between" style={{ borderRadius: '0' }}>
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <span className="text-red-700 font-medium">{error}</span>
          </div>
          <button
            onClick={fetchHealthData}
            className="px-3 py-1 bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors"
            style={{ borderRadius: '0' }}
          >
            Retry
          </button>
        </div>
      )}

      {healthData && (
        <>
          {/* Overall Status Card */}
          <div className="mb-8">
            <div className="bg-white border border-slate-200 p-6 shadow-sm" style={{ borderRadius: '0' }}>
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-100 border border-blue-200" style={{ borderRadius: '0' }}>
                  {getStatusDisplay(getOverallStatus()).icon && (
                    React.createElement(getStatusDisplay(getOverallStatus()).icon, {
                      className: `h-8 w-8 ${getStatusDisplay(getOverallStatus()).iconColor}`
                    })
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold azure-text">Overall System Status</h3>
                  <p className="text-sm text-blue-700">All system components and services</p>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center px-4 py-2 text-sm font-medium ${getStatusDisplay(getOverallStatus()).bgColor} ${getStatusDisplay(getOverallStatus()).textColor} border ${getStatusDisplay(getOverallStatus()).borderColor}`} style={{ borderRadius: '0' }}>
                    {getOverallStatus()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Services Grid */}
          <div className="space-y-8">
            {/* Microservices Section */}
            <div>
              <h3 className="text-lg font-bold azure-text mb-4 pb-2 border-b border-slate-200">
                Microservices
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.values(serviceConfig.services).map(service =>
                  healthData[service.id] && renderServiceStatus(service.id, healthData[service.id])
                )}
              </div>
            </div>

            {/* Infrastructure Section */}
            <div>
              <h3 className="text-lg font-bold azure-text mb-4 pb-2 border-b border-slate-200">
                Infrastructure
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.values(serviceConfig.infrastructure).map(service =>
                  healthData[service.id] && renderServiceStatus(service.id, healthData[service.id])
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HealthDashboard;
